import React from 'react';

import classNames from 'classnames';

import { Header, Layout } from 'components/Layout';
import { NavigationBar } from 'components/NavigationBar';

import styles from './PrivacyPolicyPage.module.scss';

const PrivacyPolicyPage = () => {
  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <Header hasBoxShadow>
            <NavigationBar hasFullLogo />
          </Header>
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>FirstIn Patient Privacy Policy</h1>

          <p className={classNames(styles.paragraph, styles.meta)}>
            <strong>Effective date:</strong> August 13, 2026
          </p>

          <div className={styles.notice}>
            <strong>Important:</strong> FirstIn provides scheduling technology
            to dental practices. Your dental practice remains responsible for
            your dental care and its patient records. This Policy does not
            replace the practice's Notice of Privacy Practices or other privacy
            notice.
          </div>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>1. Scope</h2>

            <p className={styles.paragraph}>
              This Patient Privacy Policy explains how FirstIn ("FirstIn," "we,"
              "us," or "our") collects, uses, discloses, retains, and protects
              information when patients and their authorized representatives use
              a FirstIn-powered website, online scheduling widget, booking link,
              verification service, patient account, or related communication
              service (collectively, the "Patient Services"). FirstIn is
              operated by FirstIn Practice Solutions LTD.
            </p>

            <p className={styles.paragraph}>
              The Patient Services may be embedded within a dental practice's
              website. The practice separately controls its website, clinical
              services, patient records, financial policies, and its own privacy
              practices. Review the selected practice's privacy notice and
              Notice of Privacy Practices, where applicable.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              2. FirstIn's role and the dental practice's role
            </h2>

            <p className={styles.paragraph}>
              FirstIn is a scheduling technology provider, not a dentist,
              healthcare provider, insurer, or emergency service. Depending on
              the context and applicable law, FirstIn may process information
              for a dental practice as its service provider, processor, or HIPAA
              business associate. A dental practice may be a HIPAA covered
              entity or otherwise responsible for determining how patient
              information is used for care, payment, and practice operations.
            </p>

            <p className={styles.paragraph}>
              Questions about treatment, diagnoses, clinical records, insurance
              acceptance, prices, refunds, cancellations, or the practice's
              privacy practices should be directed to the dental practice.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              3. Information we collect
            </h2>

            <h3 className={styles['subsection-title']}>
              Information you provide
            </h3>

            <p className={styles.paragraph}>
              Depending on the practice and booking workflow, we may collect:
            </p>

            <ul className={styles.list}>
              <li>
                Patient name, date of birth, phone number, email address, home
                address, postal or ZIP code, and country code.
              </li>
              <li>
                Appointment reason, requested service, preferred practice
                location, provider or provider type, requested date and time,
                and scheduling preferences.
              </li>
              <li>
                Insurance provider selection, insurance-related information, and
                self-pay status.
              </li>
              <li>
                Free-text notes, which may include symptoms, pain, areas of
                concern, requested accommodations, insurance details, or
                requests for an earlier appointment.
              </li>
              <li>
                Information about children, dependants, family members, or other
                patients when an authorized person schedules for them.
              </li>
              <li>
                Verification codes and responses, sign-in details, account
                information, and information used to confirm control of a phone
                number or account.
              </li>
              <li>
                Communications, support requests, consent choices, and policy
                acknowledgments.
              </li>
              <li>
                Payment or deposit information if a practice enables a payment
                feature. Payment-card details may be collected directly by a
                payment processor rather than stored by FirstIn.
              </li>
            </ul>

            <p className={styles.paragraph}>
              Do not enter urgent or emergency information in an appointment
              note. The note field is not an emergency communication channel.
            </p>

            <h3 className={styles['subsection-title']}>
              Information received from practices and connected systems
            </h3>

            <p className={styles.paragraph}>
              To match a patient, show existing information, check availability,
              and complete a booking, we may receive information from the
              selected practice and its practice-management, scheduling,
              communications, insurance, identity, or payment systems. This may
              include patient demographics, contact information, appointment
              history or status, providers, services, availability, insurance
              information, family or account relationships, and identifiers used
              by the practice.
            </p>

            <h3 className={styles['subsection-title']}>
              Information from sign-in providers
            </h3>

            <p className={styles.paragraph}>
              If you choose to sign in or register using a provider such as
              Google or Facebook, we may receive the information that provider
              makes available with your permission, such as your name, email
              address, account identifier, and authentication result.
            </p>

            <p className={styles.paragraph}>
              The sign-in provider processes information under its own terms and
              privacy policy.
            </p>

            <h3 className={styles['subsection-title']}>
              Technical and usage information
            </h3>

            <p className={styles.paragraph}>
              We may collect IP address, browser and device type, operating
              system, language, approximate region, referral source, widget or
              practice identifier, timestamps, pages or steps viewed, button
              interactions, error information, security events, session
              identifiers, verification attempts, and similar diagnostic data
              needed to operate, secure, and improve the Patient Services.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              4. How we use information
            </h2>

            <p className={styles.paragraph}>We use information to:</p>

            <ul className={styles.list}>
              <li>
                Display practice services, providers, locations, and appointment
                availability.
              </li>
              <li>
                Match information you submit with a practice's patient records
                and display information for confirmation.
              </li>
              <li>
                Create, request, confirm, reschedule, or cancel appointments as
                enabled by the practice.
              </li>
              <li>
                Write appointment and patient information to connected practice
                systems.
              </li>
              <li>
                Verify phone numbers and accounts, prevent unauthorized access,
                detect fraud, and protect security.
              </li>
              <li>
                Send verification codes and appointment-related texts, calls, or
                emails.
              </li>
              <li>
                Process insurance selections, self-pay choices, deposits,
                payments, refunds, or fee authorizations when enabled.
              </li>
              <li>
                Provide support, troubleshoot errors, maintain availability, and
                improve accessibility and performance.
              </li>
              <li>
                Maintain consent, policy-version, audit, and transaction
                records.
              </li>
              <li>
                Comply with law, contracts, legal process, and privacy or
                security obligations.
              </li>
            </ul>

            <p className={styles.paragraph}>
              FirstIn does not use patient scheduling information for targeted
              advertising and does not authorize advertising or analytics
              vendors to use appointment details, patient identifiers, insurance
              information, or health-related notes for targeted advertising.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              5. Patient matching and identity verification
            </h2>

            <p className={styles.paragraph}>
              A practice may configure FirstIn to match a patient using
              information such as date of birth and phone number. We may send a
              one-time verification code by text message before displaying
              matched patient information.
            </p>

            <p className={styles.paragraph}>
              We may record that a code was requested, delivered or failed, when
              it expired, the number of attempts, and whether verification
              succeeded. Verification reduces risk but cannot guarantee
              identity.
            </p>

            <p className={styles.paragraph}>
              If the displayed patient is not you or a person you are authorized
              to represent, do not proceed. Use the available "not you" option
              or contact the practice. Repeated or suspicious lookup attempts
              may be limited or investigated.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              6. Appointment communications and marketing
            </h2>

            <p className={styles.paragraph}>
              We and the selected practice may use the phone number and email
              address associated with a booking to send verification codes,
              appointment confirmations, reminders, changes, cancellations,
              wait-list messages, payment notices, and other transactional
              communications. Message and data rates may apply. Delivery is not
              guaranteed.
            </p>

            <p className={styles.paragraph}>
              Marketing messages require any consent required by law and should
              be presented separately from scheduling. Declining marketing must
              not prevent a patient from receiving necessary transactional
              communications or using the scheduling service.
            </p>

            <p className={styles.paragraph}>
              Follow the instructions in a marketing message to withdraw
              marketing consent. Contact the practice to manage essential
              appointment communications.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              7. Embedded widgets, cookies, analytics, and practice-site
              tracking
            </h2>

            <p className={styles.paragraph}>
              A FirstIn widget may appear inside a website controlled by a
              dental practice. The practice may operate cookies, Google
              Analytics, Meta Pixel, advertising tags, session-replay tools, tag
              managers, or similar technologies on the surrounding website.
            </p>

            <p className={styles.paragraph}>
              Those technologies may collect information such as the page
              visited, IP address, device or advertising identifiers, referral
              source, and interactions with the practice's website. The
              practice's privacy notice and consent tool govern that independent
              processing.
            </p>

            <p className={styles.paragraph}>
              Browser security generally restricts a practice's surrounding
              website from directly reading fields inside a cross-origin FirstIn
              widget. FirstIn also seeks to limit host-page events to
              non-identifying operational signals. However, identifiers combined
              with a visit to a dental website or initiation of a scheduling
              flow may still allow sensitive inferences.
            </p>

            <p className={styles.paragraph}>
              FirstIn prohibits practices and vendors from using scripts,
              pixels, analytics events, URL parameters, data-layer variables,
              session replay, or similar tools to capture patient identifiers,
              dates of birth, phone numbers, appointment reasons, providers,
              insurance choices, symptoms, health notes, appointment dates, or
              other sensitive widget data for advertising or profiling.
            </p>

            <p className={styles.paragraph}>
              FirstIn may use strictly necessary cookies or similar technologies
              for security, session continuity, fraud prevention, load
              balancing, and core scheduling functions. We may also use
              privacy-protective measurement for aggregate reliability and
              performance, subject to applicable law and contractual safeguards.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              8. How we disclose information
            </h2>

            <p className={styles.paragraph}>We may disclose information:</p>

            <ul className={styles.list}>
              <li>
                To the selected dental practice, its authorized personnel, and
                its connected practice-management or scheduling systems.
              </li>
              <li>
                To vendors that provide hosting, cloud storage, communications,
                identity verification, security, customer support,
                insurance-directory, analytics, and payment services under
                appropriate contractual restrictions.
              </li>
              <li>
                To Google, Facebook, or another sign-in provider when you choose
                that sign-in method.
              </li>
              <li>
                To a payment processor, card network, or financial institution
                when a payment feature is used.
              </li>
              <li>
                As required by law, legal process, professional regulation, or
                to protect patients, FirstIn, practices, or others from fraud,
                abuse, security threats, or harm.
              </li>
              <li>
                In connection with a merger, financing, restructuring, sale, or
                transfer, subject to applicable legal protections.
              </li>
              <li>With your direction or consent.</li>
            </ul>

            <p className={styles.paragraph}>
              We do not sell patient scheduling information for monetary
              consideration. We do not share it for cross-context behavioural
              advertising or targeted advertising as those terms are defined by
              applicable privacy laws.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              9. HIPAA and practice privacy notices
            </h2>

            <p className={styles.paragraph}>
              When FirstIn handles protected health information for a
              HIPAA-regulated practice, we do so under the applicable business
              associate agreement and the practice's instructions, subject to
              HIPAA.
            </p>

            <p className={styles.paragraph}>
              The practice remains responsible for its Notice of Privacy
              Practices and for responding to patient requests concerning the
              designated record set, unless it asks FirstIn to assist.
            </p>

            <p className={styles.paragraph}>
              Not all information or organizations are governed by HIPAA. Other
              federal, state, provincial, or territorial health and consumer
              privacy laws may apply.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              10. Consumer health data
            </h2>

            <p className={styles.paragraph}>
              Information about seeking dental services, appointment reasons,
              symptoms, treatment interests, providers, insurance, and related
              identifiers may constitute consumer health data under certain
              laws.
            </p>

            <p className={styles.paragraph}>
              FirstIn collects and processes this information to provide the
              Patient Services, comply with law, protect security, and carry out
              other purposes described at or before collection. FirstIn does not
              use consumer health data for targeted advertising or sell it
              without the separate authorization required by applicable law.
            </p>

            <p className={styles.paragraph}>
              This Policy also serves as FirstIn's Consumer Health Data Privacy
              Policy where applicable. The categories of consumer health data we
              collect are described in Section 3; the sources are patients,
              authorized representatives, dental practices, connected systems,
              sign-in providers, devices, and service providers; the purposes
              are described in Sections 4 through 7; and the categories of
              recipients are described in Section 8.
            </p>

            <p className={styles.paragraph}>
              Where required by applicable consumer health data law, you may
              request confirmation of whether FirstIn collects, shares, or sells
              your consumer health data; access that data; receive a list of
              third parties and affiliates with which it was shared or sold;
              withdraw consent; request deletion; or appeal a refusal of your
              request.
            </p>

            <p className={styles.paragraph}>
              FirstIn does not sell consumer health data or use it for targeted
              advertising. Submit these requests or appeals to{' '}
              <a className={styles.link} href="mailto:privacy@firstin.io">
                privacy@firstin.io
              </a>
              .
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              11. Data location and cross-border processing
            </h2>

            <p className={styles.paragraph}>
              FirstIn, practices, and service providers may process information
              in Canada, the United States, and other jurisdictions. Information
              may therefore be subject to the laws and lawful access authorities
              of the place where it is processed.
            </p>

            <p className={styles.paragraph}>
              We use contractual, organizational, and technical safeguards
              appropriate to the information and applicable law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>12. Retention</h2>

            <p className={styles.paragraph}>
              We retain information for as long as reasonably necessary to
              provide the Patient Services, maintain appointment and consent
              records, support practices, prevent fraud, resolve disputes,
              comply with law and contracts, and protect security.
            </p>

            <p className={styles.paragraph}>
              Retention varies by record type and practice configuration.
              Verification codes are short-lived, while verification logs,
              booking records, consent evidence, security logs, and information
              written to a practice system may be retained longer. The dental
              practice controls retention of its patient record.
            </p>

            <p className={styles.paragraph}>
              When information is no longer required, we delete, de-identify, or
              securely dispose of it, subject to legal, backup, security, and
              technical limitations.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>13. Security</h2>

            <p className={styles.paragraph}>
              We use administrative, technical, and physical safeguards designed
              to protect information, including access controls, logging,
              encryption where appropriate, vendor controls, and security
              monitoring.
            </p>

            <p className={styles.paragraph}>
              No system is perfectly secure, and we cannot guarantee that
              unauthorized access, loss, or misuse will never occur.
            </p>

            <p className={styles.paragraph}>
              Protect verification codes and devices used to receive them.
              Notify the practice or FirstIn promptly if you believe a booking
              or patient account has been accessed without authorization.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>14. Your privacy rights</h2>

            <p className={styles.paragraph}>
              Depending on your location and the applicable law, you may have
              rights to request access, correction, deletion, portability,
              restriction, withdrawal of consent, or information about
              disclosures; to opt out of certain processing; or to appeal a
              decision.
            </p>

            <p className={styles.paragraph}>
              Some rights may need to be exercised through the dental practice
              because it controls the patient record. We may verify your
              identity and authority before acting.
            </p>

            <p className={styles.paragraph}>
              Rights are subject to exceptions, including obligations to
              preserve clinical, billing, security, fraud-prevention, legal, or
              transaction records. You may also complain to the relevant privacy
              or data-protection regulator.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              15. Children and scheduling for others
            </h2>

            <p className={styles.paragraph}>
              The Patient Services may be used by a parent, guardian, or other
              authorized person to schedule for a child or dependant. Do not
              submit information for another person unless you are legally
              authorized to do so.
            </p>

            <p className={styles.paragraph}>
              Practices determine whether additional consent, guardianship, or
              identity verification is required.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>
              16. Changes to this Policy
            </h2>

            <p className={styles.paragraph}>
              We may update this Policy to reflect changes in the Patient
              Services, practices, law, or technology.
            </p>

            <p className={styles.paragraph}>
              We will post the updated version with a new effective date and
              provide additional notice when required.
            </p>

            <p className={styles.paragraph}>
              The version displayed or linked during a booking applies to that
              interaction.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles['section-title']}>17. Contact</h2>

            <p className={styles.paragraph}>
              For questions about an appointment, clinical record, treatment,
              insurance, payment, cancellation, or the practice's privacy
              notice, contact the selected dental practice.
            </p>

            <p className={styles.paragraph}>
              For questions about FirstIn's privacy practices, to submit a
              privacy request, or to appeal a privacy decision, contact:
            </p>

            <p className={styles.paragraph}>
              <strong>
                Privacy Officer
                <br />
                FirstIn Practice Solutions LTD.
                <br />
                Edmonton, Alberta, Canada
                <br />
                <a className={styles.link} href="mailto:privacy@firstin.io">
                  privacy@firstin.io
                </a>
              </strong>
            </p>

            <p className={classNames(styles.paragraph, styles['footer-line'])}>
              FirstIn Practice Solutions LTD. | Edmonton, Alberta, Canada |{' '}
              <a className={styles.link} href="mailto:privacy@firstin.io">
                privacy@firstin.io
              </a>
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicyPage;
